import {
    Connection,
    ConnectionType,
    DataType,
    Location,
    MeasurementsResult,
    MeterType
} from '@flowmaps-com/flowmaps-typescriptmodels';
import {
    buildMeterDisplayHierarchy,
    calculatedResidualSourceMeterNumber,
    effectiveMeterType,
    isCalculatedResidual,
    isMeterVisible
} from '../refdata/meters/submeter-role.utils';
import {MeterTreeName} from '../dashboard/dashboard.types';
import {Entity} from '../../handlers/entity';
import {TranslateDirective} from '../../common/utils/translate.directive';
import {RefdataUtils} from '../refdata/refdata-utils';

const METER_TREE_NAME_STORAGE_PREFIX = 'flowmaps.meterTreeName';

export function hasVisibleIntermediateMeters(location: Location | null | undefined, connectionType: ConnectionType): boolean {
    return location?.connections?.some(connection => connection.info?.connectionType === connectionType
        && connection.meters?.some(meter => effectiveMeterType(meter) === MeterType.INTERMEDIATE
            && isMeterVisible(meter)
            && (meter.details as any)?.chargePoint !== true)) ?? false;
}

export function linkedElectricityConnectionId(connection: Connection,
                                              locationConnections: Connection[] = []): string | null {
    if (connection?.info?.connectionType !== ConnectionType.ChargePoint) return null;

    const configuredConnectionId = (connection.info as any).electricityConnectionId;
    if (configuredConnectionId) return configuredConnectionId;

    const electricityConnections = locationConnections.filter(candidate =>
        candidate.info?.connectionType === ConnectionType.Electricity);
    return electricityConnections.length === 1 ? electricityConnections[0].connectionId : null;
}

export function hasEligibleMeterTreeMeters(location: Location | null | undefined): boolean {
    const connections = location?.connections || [];
    return hasVisibleIntermediateMeters(location, ConnectionType.Electricity)
        || hasVisibleIntermediateMeters(location, ConnectionType.Water)
        || connections.some(connection => connection.info?.connectionType === ConnectionType.ChargePoint
            && !!linkedElectricityConnectionId(connection, connections)
            && (connection.meters || []).some(meter => isMeterVisible(meter)
                && [MeterType.PRIMARY, MeterType.INTERMEDIATE].includes(effectiveMeterType(meter))));
}

export function defaultIntermediateConsumptionType(location: Location | null | undefined): DataType {
    return !hasVisibleIntermediateMeters(location, ConnectionType.Electricity)
        && hasVisibleIntermediateMeters(location, ConnectionType.Water)
        ? DataType.waterIntermediateConsumption
        : DataType.electricityIntermediateConsumption;
}

export function meterTreeNameStorageKey(userId?: string): string {
    return `${METER_TREE_NAME_STORAGE_PREFIX}.${userId || 'anonymous'}`;
}

export function intermediateConsumptionTypesWithData(location: Location | null | undefined,
                                                     measurements: MeasurementsResult[] | null | undefined): DataType[] {
    const measurementsByMeterId = new Map((measurements || [])
        .filter(measurement => !!measurement.entityId)
        .map(measurement => [measurement.entityId, measurement]));
    const availableTypes = new Set<DataType>();
    for (const connection of location?.connections || []) {
        const dataType = connection.info?.connectionType === ConnectionType.Electricity
            ? DataType.electricityIntermediateConsumption
            : connection.info?.connectionType === ConnectionType.Water
                ? DataType.waterIntermediateConsumption : null;
        if (!dataType || availableTypes.has(dataType)) continue;
        const visibleMeterIds = buildMeterDisplayHierarchy(connection.meters || []).visibleIds;
        if ((connection.meters || []).some(meter => meter.meterId && visibleMeterIds.has(meter.meterId)
            && isMeterVisible(meter) && effectiveMeterType(meter) === MeterType.INTERMEDIATE
            && (connection.info?.connectionType !== ConnectionType.Electricity || (meter.details as any)?.chargePoint !== true)
            && (measurementsByMeterId.get(meter.meterId)?.measurements?.[dataType] || [])
                .some(measurement => (measurement.value || 0) > 0))) {
            availableTypes.add(dataType);
        }
    }
    return [...availableTypes];
}

/** Keep a usable selection; fall back only when the other submeter type has data. */
export function intermediateConsumptionTypeForPeriod(availableTypes: DataType[], selectedType: DataType): DataType {
    return availableTypes.includes(selectedType) || availableTypes.length !== 1 ? selectedType : availableTypes[0];
}

export function loadMeterTreeNamePreference(storage: Storage, userId?: string): MeterTreeName | null {
    try {
        const value = storage.getItem(meterTreeNameStorageKey(userId)) as MeterTreeName;
        return Object.values(MeterTreeName).includes(value) ? value : null;
    } catch {
        return null;
    }
}

export function saveMeterTreeNamePreference(storage: Storage, selectedName: MeterTreeName, userId?: string): void {
    try {
        storage.setItem(meterTreeNameStorageKey(userId), selectedName);
    } catch {
        return;
    }
}

export function splitFilterValues(value: string | null | undefined): string[] {
    return (value || '').split(';').map(item => item.trim()).filter(Boolean);
}

export function meterTreeName(entity: Entity, selectedName: MeterTreeName = MeterTreeName.Name): string {
    const residualMeter = isCalculatedResidual(entity.meter);
    const defaultName = residualMeter
        ? entity.meter?.details?.label || TranslateDirective.getTranslation('Unallocated consumption', true) as string
        : RefdataUtils.meterFormatter(entity);
    if (selectedName === MeterTreeName.SourceMeterNumber) {
        if (residualMeter) return calculatedResidualSourceMeterNumber(entity.connection?.connectionId);
        return (entity.meter?.info?.meterNumbers || []).filter(Boolean).join(', ')
            || entity.meter?.info?.accessPointId
            || defaultName;
    }
    if (selectedName === MeterTreeName.Tenant) {
        return splitFilterValues(RefdataUtils.getMeterMetadataValue(entity.meter, 'tenant')).join(', ') || defaultName;
    }
    return entity.meter?.details?.label || (residualMeter ? defaultName : entity.meter?.info?.label) || defaultName;
}

export function meterTreeNameSelectable(selectedName: MeterTreeName, meters: Entity[] = []): boolean {
    switch (selectedName) {
        case MeterTreeName.Tenant:
            return meters.some(entity => splitFilterValues(RefdataUtils.getMeterMetadataValue(entity.meter, 'tenant')).length > 0);
        case MeterTreeName.SourceMeterNumber:
            return meters.some(entity => (entity.meter?.info?.meterNumbers || []).some(Boolean)
                || !!entity.meter?.info?.accessPointId);
        default:
            return true;
    }
}
